/* ─────────────────────────────────────────────
   LENS LIVE BADGE — optional, fail-open cross-app hook (spec Phase 4 / §7)
   ─────────────────────────────────────────────

   Locus stays GPS-only; this is the ONLY place it touches `lens`, and it does so
   purely as a client-side, pull-based, fail-open contract. There is NO
   compile-time or runtime dependency on lens: this module imports nothing from
   `apps/lens`, talks to it only over `fetch`, and any failure (lens down, flag
   off, non-200, malformed body, network/timeout) results in NO change to Locus
   behavior — the cohort renders exactly as it does today.

   Discovery model (mirrors Locus's own by-prefix cohort poll, ADR 0003): on each
   cohort refresh we make ONE unsigned batch call
     GET <lensBase>/api/v1/live?group=<g1-g2>
   for the whole cohort and remember which devices are live. lens keys each live
   stream by the device's OPAQUE Locus cohort id (HMAC(SERVER_COHORT_SECRET,
   token), ADR 0015) — the SAME id the cohort `/recent` snapshot carries for every
   peer (in its wire `token` field). So this map is keyed by that opaque id, and
   the cohort row renderer (ui/sidebar.ts renderByDeviceList, keyed on `d.token`
   which IS the opaque id for peers) paints a "🔴 live / watch" indicator on any
   PEER row that is live. The operator's own raw-token-seeded local row no longer
   matches discovery — that's fine (the operator already knows they're live; the
   self-badge is intentionally dropped — Locus ADR 0028 / spec Decided #2).

   The id is SELF-ASSERTED by the live device (it reads its own opaque id off its
   Locus cohort self-echo and forwards it to lens at live/start). lens trusts it
   as-is for v1 (fail-open cosmetic; a Locus-signed attestation is the documented
   hardening — ADR 0028).

   The "watch" affordance opens a lens PLAYER deep-link
     <lensBase>/#/w?g=<groupPath>&c=<cohortId>
   (Decided #3) — NOT the raw signed `playback` manifest URL. That manifest
   downloads instead of playing (browsers), dies with its ~120s signed-token TTL,
   and is unplayable once saved (relative segment URLs). The deep-link route plays
   in lens's hls.js viewer and self-re-mints by re-polling discovery, using the
   only two handles the badge has — the group prefix and the opaque cohort id (the
   raw token is hidden, so lens live/:token is not addressable from here). We still
   require a `playback` to be present at poll time as the fail-safe (no badge
   without a watchable stream), we just don't put it in the href.

   Config (BUILD-TIME via Vite — Direct Upload ignores wrangler [vars], so a
   runtime var would be undefined in prod; bake it at build time and redeploy):
     VITE_LENS_BADGE  — feature flag. '1' enables the badge; anything else
                        (incl. unset) keeps it OFF. Ships OFF by default so the
                        badge is dormant until a build-time env + redeploy.
     VITE_LENS_BASE   — lens origin (no trailing slash). Defaults to the prod
                        lens project subdomain when unset.
*/

// The lens prod base. Canonical domain is the Pages custom domain on the
// waybeam.dev zone (proxied CNAME → lens-cmm.pages.dev), mirroring locus.
const DEFAULT_LENS_BASE = 'https://lens.waybeam.dev';

// Read the two build-time Vite envs. import.meta.env is statically replaced at
// build time; an unset var is `undefined`, so both have safe fallbacks.
const RAW_BASE = (import.meta.env.VITE_LENS_BASE as string | undefined) ?? '';
export const LENS_BASE = (RAW_BASE.trim() || DEFAULT_LENS_BASE).replace(/\/+$/, '');
export const LENS_BADGE_ENABLED = (import.meta.env.VITE_LENS_BADGE as string | undefined) === '1';

// Abort the badge fetch fast — the cohort render must never wait on lens. This
// is well under the 30 s cohort cadence; on timeout we simply skip this cycle.
const LENS_FETCH_TIMEOUT_MS = 4000;

// The group prefix the device was discovered under (Locus `g1-g2` form). It's
// the only watch handle besides the cohort id — the deep-link re-polls discovery
// by this prefix to re-mint the stream. (All entries in one poll share the same
// prefix; stored per-entry so lensWatchUrl needs no extra argument.)
interface LiveEntry { group: string }

// opaque cohort id → live entry. Keyed by the OPAQUE Locus cohort id lens
// discovery now returns (HMAC(SERVER_COHORT_SECRET, token), ADR 0015) — the same
// id the cohort `/recent` snapshot carries for every PEER. So a peer's cohort
// row (keyed on `d.token` = its opaque id) matches and lights up; the operator's
// own raw-token-seeded local row does NOT (intentionally — the self-badge is
// dropped, Decided #2). Always empty when the flag is OFF, so no false badge.
let liveById = new Map<string, LiveEntry>();

/** Is this cohort id (the row's `d.token`, an opaque cohort id for peers)
 *  currently flagged live by lens? (Always false when the flag is OFF — the map
 *  stays empty.) */
export function isDeviceLive(cohortId: string): boolean {
  return liveById.has(cohortId);
}

/** The "watch" URL for a live cohort id — a lens PLAYER deep-link
 *  `<lensBase>/#/w?g=<groupPath>&c=<cohortId>` (Decided #3), NOT the raw signed
 *  manifest (which downloads, expires, and won't play). The lens route plays in
 *  hls.js and self-re-mints via discovery. Empty string when not live (the badge
 *  isn't rendered then). */
export function lensWatchUrl(cohortId: string): string {
  const entry = liveById.get(cohortId);
  if (!entry) return '';
  return `${LENS_BASE}/#/w?g=${encodeURIComponent(entry.group)}&c=${encodeURIComponent(cohortId)}`;
}

/**
 * Fire-and-forget refresh of the live-badge set for a cohort group prefix.
 *
 * FAIL-OPEN: returns silently (leaving the previous set, or an empty one) on the
 * flag being off, a bad prefix, any network/timeout error, a non-200, or a
 * malformed body. Never throws; callers do not await it for correctness — the
 * cohort render proceeds regardless and simply re-paints when the next cohort
 * refresh fires (the badge is eventually-consistent, like the cohort itself).
 *
 * @param groupPath the cohort prefix in Locus's `g1-g2` form (group.join('-')).
 */
export async function refreshLiveBadges(groupPath: string | null): Promise<void> {
  if (!LENS_BADGE_ENABLED) return;          // dormant unless built with VITE_LENS_BADGE=1
  if (!groupPath) return;

  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), LENS_FETCH_TIMEOUT_MS);
  try {
    const url = `${LENS_BASE}/api/v1/live?group=${encodeURIComponent(groupPath)}`;
    const res = await fetch(url, { signal: ctrl.signal, headers: { accept: 'application/json' } });
    if (!res.ok) return;                    // 4xx/5xx/429 → leave the set unchanged-ish (rebuilt below only on success)
    const body: any = await res.json();
    const devices = Array.isArray(body?.devices) ? body.devices : [];
    const next = new Map<string, LiveEntry>();
    for (const d of devices) {
      // `token` is now the device's OPAQUE cohort id (lens keys discovery on it).
      const cohortId = typeof d?.token === 'string' ? d.token : null;
      // The "watch" link is a lens player deep-link built from groupPath +
      // cohortId (see lensWatchUrl). We don't put the manifest in the href, but
      // we still require discovery to have minted a `playback` here as the
      // fail-safe (no badge without a currently-watchable stream).
      const hasStream = typeof d?.playback === 'string' && d.playback.length > 0;
      if (!cohortId || !hasStream) continue;
      next.set(cohortId, { group: groupPath });
    }
    liveById = next;
  } catch {
    // Network error, abort/timeout, malformed JSON — fail open, no behavior change.
    return;
  } finally {
    clearTimeout(timer);
  }
}
